import type { Board } from "../board";
import { Mob } from "../mob";
import type { PhysicalThing } from "../physicalThing";
import { randomFloat, randomInt, shuffleArray, weightedRandomPickN } from "../random";
import type { MobClass, MobGenerationConfig } from "./mobGenerationConfig";
import { MapMarker } from "../things/mapMarker";
import { BigSquid } from "../mobs/bigSquid";
import type { Thing } from "../thing";
import { Text } from "../things/text";
import { LevelChangerThing } from "../things/levelChangerThing";
import { LootBox } from "../things/lootBox";
import { MetalLootBox } from "../things/metalLootBox";
import { MedicalLootBox } from "../things/medicalLootBox";
import { KeyLootBox } from "../things/keyLootBox";
import { BigContainer } from "../things/bigContainer";
import { MultiTileThingController } from "../things/multitile/multiTileThingController";
import type { MultiTileThingCore } from "../things/multitile/multiTileThingCore";
import { Portal2 } from "../things/portal2/portal2";
import { ReinforcedPortal2 } from "../things/portal2/reinforcedPortal2";
import { RobotSpawner } from "../things/robotSpawner";
import { XY } from "../xy";
import { Key } from "../items/key";
import { Medikit } from "../items/medikit";
import type { LootTable } from "./lootTable";
import { Prefab } from "./prefab";
import { SimpleBoard } from "./simpleBoard";
import { noise } from "./simplexNoise";
import { VendingMachine } from "../things/vendingMachine";
import type { VendingTable } from "./vendingTable";
import type { StockEntry } from "../vending/stockEntry";
import { getItemRegistryKeys, getWeaponClassRegistryKeys } from "../serialization";

// --- Cell selection ---

export function getRandomCell(board: Board, rng: () => number): XY {
	return new XY(randomInt(0, board.width - 1, rng), randomInt(0, board.height - 1, rng));
}

export function getRandomEmptyCell(board: Board, rng: () => number): XY | null {
	return getRandomEmptyCellInArea(board.getAllXYs(), board, rng);
}

export function getRandomEmptyCellInArea(area: XY[], board: Board, rng: () => number): XY | null {
	return getRandomXYByCriteriaInArea(area, (xy) => board.isCellEmpty(xy), rng);
}

export function getRandomXYByCriteria(board: Board, criteria: (xy: XY) => boolean, rng: () => number): XY | null {
	return getRandomXYByCriteriaInArea(board.getAllXYs(), criteria, rng);
}

export function getRandomXYByCriteriaInArea(area: XY[], criteria: (xy: XY) => boolean, rng: () => number): XY | null {
	const shuffled = [...area];
	for (let i = shuffled.length - 1; i > 0; i--) {
		const j = Math.floor(rng() * (i + 1));
		[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
	}
	return shuffled.find(criteria) ?? null;
}

// --- Simplex noise helpers ---

function generateRandomSimplexNoise(width: number, height: number, freq: number, rng: () => number): number[][] {
	const offsetX = randomInt(0, 100000, rng);
	const offsetY = randomInt(0, 100000, rng);
	const frequency = freq / width;
	const result: number[][] = [];
	for (let x = 0; x < width; x++) {
		result[x] = [];
		for (let y = 0; y < height; y++) {
			let val = noise((x + offsetX) * frequency, (y + offsetY) * frequency);
			val = (val + 1) / 2; // normalize to [0, 1]
			result[x][y] = val;
		}
	}
	return result;
}

export function applySimplexNoise(
	board: Board,
	frequency: number,
	limit: number,
	supplier: () => PhysicalThing,
	rng: () => number,
	condition?: (xy: XY) => boolean,
	noiseModifier?: (xy: XY) => number,
): void {
	const noiseGrid = generateRandomSimplexNoise(board.width, board.height, frequency, rng);
	for (let y = 0; y < board.height; y++) {
		for (let x = 0; x < board.width; x++) {
			const xy = new XY(x, y);
			const value = noiseGrid[x][y] + (noiseModifier ? noiseModifier(xy) : 0);
			if (value < limit) continue;
			if (!board.isPassable(xy)) continue;
			if (board.getThingsAt(xy).some((t) => t instanceof LevelChangerThing)) continue;
			if (condition && !condition(xy)) continue;
			const thing = supplier();
			thing.xy = xy;
			board.addThing(thing);
		}
	}
}

export function getSimplexNoiseArea(board: Board, frequency: number, limit: number, rng: () => number): XY[] {
	const noiseGrid = generateRandomSimplexNoise(board.width, board.height, frequency, rng);
	return board.getAllXYs().filter((xy) => noiseGrid[xy.x][xy.y] >= limit);
}

// --- Prefab construction ---

export function getSingleThingPrefab(
	supplier: () => PhysicalThing,
	borderRequired: boolean,
	cornerRequired: boolean,
): Prefab {
	const board = new SimpleBoard();
	board.putThingAt(supplier(), new XY(0, 0));
	board.trim();
	const prefab = new Prefab(board);
	prefab.borderRequired = borderRequired;
	prefab.cornerRequired = cornerRequired;
	return prefab;
}

// --- Prefab placement ---

export function putPrefabOnBoard(board: Board, prefab: Prefab, rng: () => number): XY[] | null {
	return putPrefabOnArea(board, board.getAllXYs(), prefab, rng);
}

export function putPrefabOnArea(
	board: Board,
	area: XY[],
	prefab: Prefab,
	rng: () => number,
	allowedPositions?: XY[],
): XY[] | null {
	const viableXY = findXYWherePrefabFits(area, prefab, rng, allowedPositions);
	if (viableXY == null) return null;
	return prefab.addToBoard(board, viableXY);
}

export function findXYWherePrefabFits(
	area: XY[],
	prefab: Prefab,
	rng: () => number,
	allowedPositions?: XY[],
): XY | null {
	const positionsAllowed = allowedPositions ?? area;
	const allowedSet = new Set(positionsAllowed.map((xy) => `${xy.x},${xy.y}`));
	const areaSet = new Set(area.map((xy) => `${xy.x},${xy.y}`));

	const shuffled = [...area];
	for (let i = shuffled.length - 1; i > 0; i--) {
		const j = Math.floor(rng() * (i + 1));
		[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
	}

	for (const targetXY of shuffled) {
		if (!allowedSet.has(`${targetXY.x},${targetXY.y}`)) continue;
		if (prefab.fitsInPositionByCriteria(targetXY, (xy) => areaSet.has(`${xy.x},${xy.y}`))) {
			return targetXY;
		}
	}

	return null;
}

// --- Portal placement ---

export function placeRandomPortal(
	area: XY[],
	board: Board,
	rng: () => number,
	reinforced: boolean = false,
): Portal2 | null {
	const maxAttempts = 500;
	const areaSet = new Set(area.map((xy) => `${xy.x},${xy.y}`));

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		const xy = getRandomEmptyCellInArea(area, board, rng);
		if (xy == null) continue;

		const neighbors = xy.getNeighbors();
		if (neighbors.some((n) => !areaSet.has(`${n.x},${n.y}`))) continue;

		const portal = reinforced ? ReinforcedPortal2.create(board, xy) : Portal2.create(board, xy);
		if (portal != null) return portal;
	}

	return null;
}

// --- Spawning ---

export function spawnMobsWithNoise(
	board: Board,
	configs: MobGenerationConfig[],
	rng: () => number,
	criteria: (xy: XY) => boolean,
	maxNoiseIterations: number,
): void {
	for (const config of configs) {
		const area: XY[] = [];
		let iterations = 0;
		while (area.length < config.amountMin && iterations < maxNoiseIterations) {
			area.push(...getSimplexNoiseArea(board, 7, 0.95, rng).filter(criteria));
			iterations++;
		}

		const shuffled = shuffleArray(area, rng);
		const count = Math.min(config.amountMax, shuffled.length);
		for (let i = 0; i < count; i++) {
			const mob = config.mobSupplier();
			mob.xy = shuffled[i];
			board.addThing(mob);
		}
	}
}

export function spawnThings(
	board: Board,
	supplier: () => PhysicalThing,
	amount: number,
	rng: () => number,
	criteria: (xy: XY) => boolean,
): void {
	for (let i = 0; i < amount; i++) {
		const xy = getRandomXYByCriteria(board, criteria, rng);
		if (xy == null) break;
		const thing = supplier();
		thing.xy = xy;
		board.addThing(thing);
	}
}

export function populateLootBoxes(
	board: Board,
	lootTable: LootTable | null,
	metalLootTable: LootTable | null,
	rewardLevel: number,
	rng: () => number,
): void {
	const lootBoxes: LootBox[] = board.getPhysicalThings().filter((t): t is LootBox => t instanceof LootBox);

	// Populate BigContainers
	const containers = getMultiTileThings(board, BigContainer);
	for (const container of containers) {
		while (container.getLoot().length < 4) {
			const roll = rng() * 100;
			let lootBox: LootBox;
			if (roll < 50) lootBox = new MetalLootBox(new XY(0, 0));
			else if (roll < 90) lootBox = new LootBox(new XY(0, 0));
			else lootBox = new MedicalLootBox(new XY(0, 0));
			container.addLoot(lootBox);
		}
		lootBoxes.push(...container.getLoot());
	}

	for (const lootBox of lootBoxes) {
		if (lootBox.getLootList().length > 0) continue;

		if (lootBox instanceof MedicalLootBox) {
			lootBox.addLoot(new Medikit(2));
		} else if (lootBox instanceof KeyLootBox) {
			lootBox.addLoot(new Key(1));
		} else if (lootBox instanceof MetalLootBox) {
			if (metalLootTable == null) continue;
			const loot = metalLootTable.getLoot(rewardLevel, rng);
			if (loot == null) continue;
			lootBox.addLoot(loot);
		} else {
			if (lootTable == null) continue;
			const loot = lootTable.getLoot(rewardLevel, rng);
			if (loot == null) continue;
			lootBox.addLoot(loot);
		}
	}
}

export function populateVendingMachines(board: Board, vendingTable: VendingTable, rng: () => number): void {
	const machines = board.getPhysicalThings().filter((t): t is VendingMachine => t instanceof VendingMachine);

	// Validate all entries once before assigning to any machine
	const validWeaponKeys = new Set(getWeaponClassRegistryKeys());
	const validItemKeys = new Set(getItemRegistryKeys());
	for (const entry of vendingTable.entries) {
		if (!validWeaponKeys.has(entry.itemKey) && !validItemKeys.has(entry.itemKey)) {
			throw new Error(
				`Invalid vending table itemKey: "${entry.itemKey}". Not a registered weapon or known inventory item.`,
			);
		}
	}

	for (const machine of machines) {
		if (machine.mode === "sell") continue;
		if (machine.offers.length > 0) continue;

		if (vendingTable.entries.length === 0) continue;

		const weightsMap = new Map(vendingTable.entries.map((e) => [e, e.weight]));
		const picked = weightedRandomPickN(weightsMap, vendingTable.pickCount, rng);

		machine.offers = [...picked].map((entry) => ({
			itemKey: entry.itemKey,
			packSize: entry.packSize,
			price: entry.price,
			stockLeft: entry.stock,
		}));
	}
}

export function removeExcessiveThings<T extends Thing>(
	board: Board,
	limit: number,
	guard: (t: Thing) => t is T,
	rng: () => number,
	protectedThings?: ReadonlySet<Thing>,
): void {
	const all = board.getAllThings().filter(guard);
	if (all.length <= limit) return;

	const removable = protectedThings ? all.filter((t) => !protectedThings.has(t)) : all;
	const toRemove = Math.min(all.length - limit, removable.length);
	if (toRemove <= 0) return;

	// Shuffle
	const shuffled = [...removable];
	for (let i = shuffled.length - 1; i > 0; i--) {
		const j = Math.floor(rng() * (i + 1));
		[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
	}

	for (let i = 0; i < toRemove; i++) {
		board.removeThing(shuffled[i]);
	}
}

// --- Multi-tile placement ---

export function placeNxNMultiTileThing(
	supplier: () => MultiTileThingCore,
	board: Board,
	size: number,
	rng: () => number,
	criteria: (xy: XY) => boolean,
): MultiTileThingController | null {
	const maxAttempts = 1000;
	const sizeVector = new XY(size - 1, size - 1);

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		const rootXY = getRandomCell(board, rng);
		const candidateArea = rootXY.getAreaTo(rootXY.add(sizeVector));
		const isValid = candidateArea.every((xy) => xy.isInside(board) && criteria(xy));
		if (!isValid) continue;
		return MultiTileThingController.addToBoard(board, supplier(), candidateArea);
	}

	return null;
}

export function placeNxNMultiTileThingAt(
	supplier: () => MultiTileThingCore,
	board: Board,
	size: number,
	rootXY: XY,
): MultiTileThingController {
	const sizeVector = new XY(size - 1, size - 1);
	const area = rootXY.getAreaTo(rootXY.add(sizeVector));
	return MultiTileThingController.addToBoard(board, supplier(), area);
}

// --- Text drawing ---

export function drawText(board: Board, startXY: XY, text: string, foreground: string = "#FFFFFF"): void {
	board.addThing(new Text(startXY, text, foreground));
}

export function drawMessage(board: Board, xy: XY, stringKey: string, foreground: string = "#FFFFFF"): void {
	board.addThing(new Text(xy, stringKey, foreground));
}

// --- Board manipulation ---

export function removeAllThingsAt(board: Board, xy: XY): void {
	const things = [...board.getThingsAt(xy)];
	for (const thing of things) {
		board.removeThing(thing);
	}
}

// --- BFS ---

export function breadthFirstSearch(start: XY, predicate: (xy: XY) => boolean): XY[] {
	const frontier: XY[] = [start];
	const visited = new Set<string>();

	while (frontier.length > 0) {
		const current = frontier.shift()!;
		const key = current.toString();
		if (visited.has(key)) continue;
		visited.add(key);

		for (const neighbor of current.getCardinalNeighbors()) {
			if (!predicate(neighbor)) continue;
			if (visited.has(neighbor.toString())) continue;
			frontier.push(neighbor);
		}
	}

	return [...visited].map((k) => XY.fromString(k));
}

// --- Cell population (zoo/prison prefabs) ---

const CELL_MOB_DENSITY_MIN = 0.4;
const CELL_MOB_DENSITY_MAX = 0.6;
const CELL_MOB_COSTS: ReadonlyMap<MobClass, number> = new Map([[BigSquid, 4]]);

/**
 * Fills every room holding a "cell" MapMarker with a single mob type, one
 * roster rotation at a time: no type repeats until every type has appeared
 * in some cell. Markers are consumed.
 */
export function populateCellsWithMobs(board: Board, roster: MobClass[], rng: () => number = Math.random): void {
	let pool: MobClass[] = [];
	for (const origin of MapMarker.consume(board, "cell")) {
		const area = breadthFirstSearch(origin, (xy) => xy.isInside(board) && board.isPassable(xy));
		if (pool.length === 0) pool = shuffleArray([...roster], rng);
		const mobClass = pool.pop()!;
		const cost = CELL_MOB_COSTS.get(mobClass) ?? 1;
		const target = Math.round(area.length * randomFloat(CELL_MOB_DENSITY_MIN, CELL_MOB_DENSITY_MAX, rng));
		for (let spawned = 0; spawned < target; spawned += cost) {
			if (!spawnCellMob(mobClass, area, board, rng)) break;
		}
	}
}

function spawnCellMob(mobClass: MobClass, area: XY[], board: Board, rng: () => number): boolean {
	if (mobClass === BigSquid) {
		// Tentacles may land on walls (they retract there), just never on other mobs.
		const canPlace = (center: XY) =>
			[center, ...center.getNeighbors()].every((xy) => xy.isInside(board) && !board.hasMob(xy));
		const center = getRandomXYByCriteriaInArea(area, canPlace, rng);
		return center != null && BigSquid.create(board, center) != null;
	}
	const xy = getRandomXYByCriteriaInArea(area, (pos) => !board.hasMob(pos), rng);
	if (!xy) return false;
	board.addThing(new mobClass(xy));
	return true;
}

// --- Gold assignment ---

export function assignGoldToPrePlacedMobs(board: Board): void {
	for (const thing of board.getAllThings()) {
		if (thing instanceof Mob) {
			thing.gold = thing.getBaseGoldValue();
		} else if (thing instanceof Portal2) {
			thing.gold = thing.getBaseGoldValue();
		} else if (thing instanceof RobotSpawner) {
			thing.gold = thing.getBaseGoldValue();
		}
	}
}

// --- Helpers ---

export function getMultiTileThings<T extends MultiTileThingCore>(board: Board, cls: new (...args: any[]) => T): T[] {
	return board.getPhysicalThings().filter((t): t is T => t instanceof cls);
}
